import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { BehaviorSubject, Observable } from 'rxjs';

import { environment } from '../../environments/environment';

/** The refresh record the API keeps per company (Company.meta.sandbox_refresh). */
export interface SandboxRefreshRecord {
  state: 'queued' | 'running' | 'done' | 'failed';
  started_at: string | null;
  finished_at: string | null;
  requested_by: string | null;
  error: string | null;
}

/** One company's block of GET /api/sandbox/status and GET /api/sandbox/companies. */
export interface SandboxCompanyStatus {
  company_id: string;
  company_name: string;
  /** Company.sandbox_enabled: may this company use the sandbox at all. */
  enabled: boolean;
  /** db_status + node_host_status both 'active' (false while a refresh runs). */
  active: boolean;
  /** When the tenant data was last copied from live (sandbox.meta). */
  last_refreshed_at: string | null;
  refreshed_by: string | null;
  refresh: SandboxRefreshRecord | null;
  state: 'idle' | 'queued' | 'running' | 'done' | 'failed';
  in_progress: boolean;
  next_allowed_at: string | null;
  can_refresh: boolean;
  blocked_reason: string | null;
}

export interface SandboxStatus {
  is_sandbox: boolean;
  refresh_interval_hours: number;
  /** When the common DB (companies, users, ...) was last copied from live. */
  common_last_refreshed_at: string | null;
  company: SandboxCompanyStatus | null;
}

export interface SandboxCompaniesResponse {
  is_sandbox: boolean;
  common_last_refreshed_at: string | null;
  companies: SandboxCompanyStatus[];
}

export interface SandboxRefreshResponse {
  message: string;
  company_id: string;
  refresh: SandboxRefreshRecord;
}

/**
 * The sandbox API (routes/globalRoutes.js -> sandboxCtrl on the backend).
 *
 * These are global routes, not /companies/:id/..., because a company is
 * deactivated while its data is being refreshed and its company routes
 * disappear exactly when the UI needs to poll.
 *
 * `holding$` is set by the banner while the signed-in client's OWN company is
 * mid-refresh; the site layout swaps the page for a holding message so the
 * user does not hit a wall of "company not found" errors.
 */
@Injectable()
export class SandboxService {
  private readonly baseUrl = `${environment.apiBaseUrl}/sandbox`;

  readonly holding$ = new BehaviorSubject<boolean>(false);

  constructor(private http: HttpClient) {}

  /** Clients always get their own company; staff / agency staff pass one. */
  status(companyId?: string | null): Observable<SandboxStatus> {
    let params = new HttpParams();
    if (companyId) params = params.set('company_id', companyId);
    return this.http.get<SandboxStatus>(`${this.baseUrl}/status`, { params });
  }

  /** Start a refresh. 202 on success; 400/403/409/503 carry error.message. */
  refresh(companyId?: string | null): Observable<SandboxRefreshResponse> {
    const body = companyId ? { company_id: companyId } : {};
    return this.http.post<SandboxRefreshResponse>(`${this.baseUrl}/refresh`, body);
  }

  /** Barkani staff only: every company with the sandbox flag. */
  companies(): Observable<SandboxCompaniesResponse> {
    return this.http.get<SandboxCompaniesResponse>(`${this.baseUrl}/companies`);
  }

  setHolding(holding: boolean): void {
    if (this.holding$.value !== holding) this.holding$.next(holding);
  }
}
